import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal, Select, Text } from '@viapeople/components'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../../utils/trpc'
import type { PropertyEditorProps } from '../../types'

export interface ExternalDataConfig {
	fileId: string
	filename: string
	matchColumn: string
	columnsToDisplay: string[]
}

const ALLOWED_FILE_TYPES = [
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-excel',
	'text/csv',
]
const FILE_EXTENSIONS = '.csv, .xls, .xlsx'
const MAX_FILE_SIZE_MB = 50
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

function ColumnConfigModal({
	isOpen,
	onClose,
	allColumns,
	initialMatchColumn,
	initialColumnsToDisplay,
	onSave,
}: {
	isOpen: boolean
	onClose: () => void
	allColumns: string[]
	initialMatchColumn: string
	initialColumnsToDisplay: string[]
	onSave: (matchColumn: string, columnsToDisplay: string[]) => void
}) {
	const [matchColumn, setMatchColumn] = useState(initialMatchColumn)
	const [selected, setSelected] = useState<string[]>(initialColumnsToDisplay)

	React.useEffect(() => {
		if (isOpen) {
			setMatchColumn(initialMatchColumn)
			setSelected(initialColumnsToDisplay)
		}
	}, [isOpen, initialMatchColumn, initialColumnsToDisplay])

	const available = allColumns.filter((col) => !selected.includes(col))
	const columnOptions = allColumns.map((h) => ({ value: h, label: h }))

	const addColumn = (col: string) => setSelected((prev) => [...prev, col])
	const addAll = () => setSelected(allColumns)
	const removeColumn = (col: string) => setSelected((prev) => prev.filter((c) => c !== col))
	const clearAll = () => setSelected([])

	const moveUp = (index: number) => {
		if (index === 0) return
		setSelected((prev) => {
			const next = [...prev]
			;[next[index - 1], next[index]] = [next[index], next[index - 1]]
			return next
		})
	}
	const moveDown = (index: number) => {
		setSelected((prev) => {
			if (index === prev.length - 1) return prev
			const next = [...prev]
			;[next[index], next[index + 1]] = [next[index + 1], next[index]]
			return next
		})
	}

	const [dragIndex, setDragIndex] = useState<number | null>(null)
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

	const handleDragStart = (index: number) => setDragIndex(index)
	const handleDragOver = (e: React.DragEvent, index: number) => {
		e.preventDefault()
		setDragOverIndex(index)
	}
	const handleDragEnd = () => {
		if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
			setSelected((prev) => {
				const next = [...prev]
				const [moved] = next.splice(dragIndex, 1)
				next.splice(dragOverIndex, 0, moved)
				return next
			})
		}
		setDragIndex(null)
		setDragOverIndex(null)
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title='Configure Columns'
			buttons={[
				{ children: 'Cancel', variant: 'secondary', onClick: onClose },
				{
					children: 'Save',
					variant: 'primary',
					onClick: () => onSave(matchColumn, selected),
					disabled: !matchColumn,
				},
			]}>
			<div className='space-y-5'>
				{/* Match column */}
				<div>
					<Text variant='small' className='block mb-1 text-left font-medium'>
						Match Column (Employee Identifier)
					</Text>
					<Text variant='xsmall' className='block mb-2 text-left text-neutral-400'>
						Column containing employee IDs to match records to users
					</Text>
					<Select
						value={matchColumn || undefined}
						onChange={setMatchColumn}
						options={columnOptions}
						placeholder='Select identifier column...'
					/>
				</div>

				<div className='h-px bg-neutral-200' />

				{/* Columns to display */}
				<div>
					<div className='flex items-center justify-between mb-2'>
						<div>
							<Text variant='small' className='block text-left font-medium'>
								Columns to Display
							</Text>
							<Text variant='xsmall' className='text-neutral-400'>
								Drag to reorder &middot; {selected.length} of {allColumns.length} selected
							</Text>
						</div>
						<div className='flex gap-1'>
							{selected.length < allColumns.length && (
								<button
									type='button'
									onClick={addAll}
									className='text-xs text-primary-600 hover:text-primary-700 px-2 py-1 rounded hover:bg-primary-50'>
									Add all
								</button>
							)}
							{selected.length > 0 && (
								<button
									type='button'
									onClick={clearAll}
									className='text-xs text-neutral-500 hover:text-error-600 px-2 py-1 rounded hover:bg-neutral-50'>
									Clear
								</button>
							)}
						</div>
					</div>

					<div className='grid grid-cols-2 gap-3'>
						{/* Available */}
						<div>
							<Text variant='xsmall' className='block mb-1 text-neutral-400 font-medium'>
								Available
							</Text>
							<div className='rounded-md border border-dashed border-neutral-200 bg-white min-h-[200px] max-h-[300px] overflow-y-auto'>
								{available.length === 0 ? (
									<div className='p-4 text-center'>
										<Text variant='xsmall' className='text-neutral-300 italic'>
											All columns added
										</Text>
									</div>
								) : (
									available.map((col) => (
										<button
											key={col}
											type='button'
											onClick={() => addColumn(col)}
											className='w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 hover:bg-primary-50 hover:text-primary-700 transition-colors text-left border-b border-neutral-50 last:border-b-0'>
											<svg
												className='w-3.5 h-3.5 flex-shrink-0 text-neutral-300'
												fill='none'
												viewBox='0 0 24 24'
												stroke='currentColor'
												strokeWidth={2}
												aria-hidden='true'>
												<path strokeLinecap='round' strokeLinejoin='round' d='M12 4.5v15m7.5-7.5h-15' />
											</svg>
											<span className='truncate'>{col}</span>
										</button>
									))
								)}
							</div>
						</div>

						{/* Selected - reorderable */}
						<div>
							<Text variant='xsmall' className='block mb-1 text-neutral-400 font-medium'>
								Selected (in order)
							</Text>
							<div className='rounded-md border border-neutral-200 bg-white min-h-[200px] max-h-[300px] overflow-y-auto'>
								{selected.length === 0 ? (
									<div className='p-4 text-center'>
										<Text variant='xsmall' className='text-neutral-300 italic'>
											Click columns to add
										</Text>
									</div>
								) : (
									<ul className='list-none m-0 p-0'>
										{selected.map((col, index) => (
											<li
												key={col}
												draggable
												onDragStart={() => handleDragStart(index)}
												onDragOver={(e) => handleDragOver(e, index)}
												onDragEnd={handleDragEnd}
												className={`flex items-center gap-1.5 px-2 py-2 text-sm border-b border-neutral-100 last:border-b-0 cursor-grab active:cursor-grabbing transition-colors ${
													dragOverIndex === index && dragIndex !== index ? 'bg-primary-50' : 'hover:bg-neutral-50'
												} ${dragIndex === index ? 'opacity-40' : ''}`}>
												<span className='text-neutral-300 flex-shrink-0 select-none' aria-hidden='true'>
													&#x2630;
												</span>
												<span className='text-[10px] text-neutral-400 tabular-nums w-4 text-center flex-shrink-0'>
													{index + 1}
												</span>
												<span className='flex-1 text-neutral-700 truncate'>{col}</span>
												<button
													type='button'
													onClick={() => moveUp(index)}
													disabled={index === 0}
													className='p-0.5 text-neutral-400 hover:text-neutral-600 disabled:opacity-20'
													title='Move up'>
													<svg
														className='w-3.5 h-3.5'
														fill='none'
														viewBox='0 0 24 24'
														stroke='currentColor'
														strokeWidth={2.5}
														aria-hidden='true'>
														<path strokeLinecap='round' strokeLinejoin='round' d='M4.5 15.75l7.5-7.5 7.5 7.5' />
													</svg>
												</button>
												<button
													type='button'
													onClick={() => moveDown(index)}
													disabled={index === selected.length - 1}
													className='p-0.5 text-neutral-400 hover:text-neutral-600 disabled:opacity-20'
													title='Move down'>
													<svg
														className='w-3.5 h-3.5'
														fill='none'
														viewBox='0 0 24 24'
														stroke='currentColor'
														strokeWidth={2.5}
														aria-hidden='true'>
														<path strokeLinecap='round' strokeLinejoin='round' d='M19.5 8.25l-7.5 7.5-7.5-7.5' />
													</svg>
												</button>
												<button
													type='button'
													onClick={() => removeColumn(col)}
													className='p-0.5 text-neutral-400 hover:text-error-500'
													title='Remove'>
													<svg
														className='w-3.5 h-3.5'
														fill='none'
														viewBox='0 0 24 24'
														stroke='currentColor'
														strokeWidth={2.5}
														aria-hidden='true'>
														<path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
													</svg>
												</button>
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Live preview */}
				{selected.length > 0 && (
					<>
						<div className='h-px bg-neutral-200' />
						<div>
							<Text variant='xsmall' className='block mb-2 text-neutral-400 font-medium'>
								Preview (display order)
							</Text>
							<div className='rounded-md border border-neutral-200 bg-white overflow-hidden'>
								{selected.map((col, i) => (
									<div
										key={col}
										className={`flex items-center gap-3 px-3 py-2 text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}`}>
										<span className='text-neutral-500 font-medium w-1/3 truncate'>{col}</span>
										<span className='text-neutral-300 italic'>Sample data</span>
									</div>
								))}
							</div>
						</div>
					</>
				)}
			</div>
		</Modal>
	)
}

export function ExternalDataSourceSelectorEditor({
	name,
	label,
	value,
	onChange,
}: PropertyEditorProps): React.ReactElement {
	const { organizationId } = useParams<{ organizationId: string }>()
	const queryClient = useQueryClient()
	const config = (value as ExternalDataConfig | undefined) || {
		fileId: '',
		filename: '',
		matchColumn: '',
		columnsToDisplay: [],
	}

	const [localConfig, setLocalConfig] = React.useState<ExternalDataConfig>(config)
	const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'processing'>('idle')
	const [uploadError, setUploadError] = useState<string | null>(null)
	const [cachedHeaders, setCachedHeaders] = useState<string[] | null>(null)
	const [isColumnModalOpen, setIsColumnModalOpen] = useState(false)

	React.useEffect(() => {
		if (value) {
			setLocalConfig(value as ExternalDataConfig)
		}
	}, [value])

	const { data: filesData, isLoading: isLoadingFiles } = useQuery({
		...trpc.admin.externalData.list.queryOptions({
			organizationId: organizationId as string,
			page: 1,
			perPage: 100,
		}),
		enabled: !!organizationId,
	})

	const { data: headersData, isLoading: isLoadingHeaders } = useQuery({
		...trpc.admin.externalData.getHeaders.queryOptions({
			organizationId: organizationId as string,
			fileId: localConfig.fileId,
		}),
		enabled: !!organizationId && !!localConfig.fileId && !cachedHeaders,
	})

	const headers = cachedHeaders || headersData

	const { mutateAsync: requestFileUpload } = useMutation({
		...trpc.admin.externalData.requestFileUpload.mutationOptions(),
	})
	const { mutateAsync: acknowledgeFileUpload } = useMutation({
		...trpc.admin.externalData.acknowledgeFileUpload.mutationOptions(),
	})
	const { mutateAsync: processFile } = useMutation({
		...trpc.admin.externalData.processFile.mutationOptions(),
	})

	const fileOptions = useMemo(() => {
		if (!filesData?.data) return []
		return filesData.data.map((f) => ({ value: f.id, label: f.filename }))
	}, [filesData])

	const handleFileChange = (newFileId: string) => {
		const selectedFile = filesData?.data.find((f) => f.id === newFileId)
		setCachedHeaders(null)
		const newConfig: ExternalDataConfig = {
			fileId: newFileId,
			filename: selectedFile?.filename || '',
			matchColumn: '',
			columnsToDisplay: [],
		}
		setLocalConfig(newConfig)
		onChange(newConfig)
	}

	const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return

		if (!ALLOWED_FILE_TYPES.includes(file.type)) {
			setUploadError(`Invalid file type. Accepted formats: ${FILE_EXTENSIONS}`)
			e.target.value = ''
			return
		}
		if (file.size > MAX_FILE_SIZE_BYTES) {
			setUploadError(`File size must not exceed ${MAX_FILE_SIZE_MB}MB.`)
			e.target.value = ''
			return
		}

		setUploadError(null)
		setUploadState('uploading')

		try {
			const { fileId, uploadUrl } = await requestFileUpload({
				organizationId: organizationId as string,
				filename: file.name,
				mimetype: file.type,
				size: file.size,
			})

			const uploadResp = await fetch(uploadUrl, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			})

			if (!uploadResp.ok) {
				throw new Error('Failed to upload file')
			}

			const { headers: fileHeaders } = await acknowledgeFileUpload({
				organizationId: organizationId as string,
				fileId,
			})

			setUploadState('processing')
			setCachedHeaders(fileHeaders)
			const newConfig: ExternalDataConfig = { fileId, filename: file.name, matchColumn: '', columnsToDisplay: [] }
			setLocalConfig(newConfig)
			onChange(newConfig)

			await processFile({
				organizationId: organizationId as string,
				fileId,
			})

			queryClient.invalidateQueries({ queryKey: [['admin', 'externalData', 'list']] })
		} catch (err) {
			setUploadError(err instanceof Error ? err.message : 'Failed to upload file.')
		} finally {
			setUploadState('idle')
			e.target.value = ''
		}
	}

	const handleClearFile = () => {
		setCachedHeaders(null)
		const newConfig: ExternalDataConfig = { fileId: '', filename: '', matchColumn: '', columnsToDisplay: [] }
		setLocalConfig(newConfig)
		onChange(newConfig)
	}

	const handleColumnModalSave = (matchColumn: string, columnsToDisplay: string[]) => {
		const newConfig: ExternalDataConfig = { ...localConfig, matchColumn, columnsToDisplay }
		setLocalConfig(newConfig)
		onChange(newConfig)
		setIsColumnModalOpen(false)
	}

	const hasExistingFiles = fileOptions.length > 0
	const isUploading = uploadState !== 'idle'
	const hasFileSelected = !!localConfig.fileId
	const headersReady = !!headers && headers.length > 0
	const headersLoading = isLoadingHeaders && !cachedHeaders
	const isConfigured = hasFileSelected && !!localConfig.matchColumn && localConfig.columnsToDisplay.length > 0

	return (
		<div key={name} className='space-y-3'>
			<Text variant='small' className='block text-left font-semibold'>
				{label || 'Data Configuration'}
			</Text>

			{!hasFileSelected ? (
				<div className='space-y-3'>
					<div className='relative'>
						{isUploading ? (
							<div className='flex items-center justify-center h-24 bg-primary-50 rounded-lg border border-primary-200'>
								<div className='flex flex-col items-center gap-2'>
									<div className='animate-spin inline-block size-5 border-2 border-primary-600 border-t-transparent rounded-full' />
									<Text variant='xsmall' className='text-primary-700 font-medium'>
										{uploadState === 'uploading' ? 'Uploading file...' : 'Processing records...'}
									</Text>
								</div>
							</div>
						) : (
							<label className='flex flex-col items-center justify-center h-24 bg-white border-2 border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50/50 transition-all group'>
								<Text variant='small' className='text-neutral-600 group-hover:text-primary-600 font-medium'>
									Upload CSV or Excel file
								</Text>
								<Text variant='xsmall' className='text-neutral-400 mt-0.5'>
									{FILE_EXTENSIONS} &middot; Max {MAX_FILE_SIZE_MB}MB
								</Text>
								<input type='file' accept={FILE_EXTENSIONS} onChange={handleFileUpload} className='hidden' />
							</label>
						)}
						{uploadError && (
							<div className='mt-2 px-3 py-2 bg-error-50 border border-error-200 rounded-md'>
								<Text variant='xsmall' className='text-error-700'>
									{uploadError}
								</Text>
							</div>
						)}
					</div>

					{hasExistingFiles && (
						<>
							<div className='flex items-center gap-3'>
								<div className='flex-1 h-px bg-neutral-200' />
								<Text variant='xsmall' className='text-neutral-400 uppercase tracking-wider font-medium'>
									or
								</Text>
								<div className='flex-1 h-px bg-neutral-200' />
							</div>
							<Select
								value={localConfig.fileId || undefined}
								onChange={handleFileChange}
								options={fileOptions}
								placeholder={isLoadingFiles ? 'Loading files...' : 'Choose existing file...'}
							/>
						</>
					)}
				</div>
			) : (
				<div className='space-y-2'>
					{/* File chip */}
					<div className='flex items-center gap-2 p-2.5 bg-primary-50 border border-primary-200 rounded-lg'>
						<div className='flex-1 min-w-0'>
							<Text variant='small' className='text-primary-800 font-medium truncate block'>
								{localConfig.filename || 'Selected file'}
							</Text>
							{headersReady && (
								<Text variant='xsmall' className='text-primary-600'>
									{headers.length} columns &middot; {localConfig.columnsToDisplay.length} displayed
								</Text>
							)}
						</div>
						<button
							type='button'
							onClick={handleClearFile}
							className='p-1 rounded hover:bg-primary-100 text-primary-600'
							title='Change file'>
							<svg
								className='w-4 h-4'
								fill='none'
								viewBox='0 0 24 24'
								stroke='currentColor'
								strokeWidth={2}
								aria-hidden='true'>
								<path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
							</svg>
						</button>
					</div>

					{/* Configure button */}
					{headersLoading ? (
						<div className='flex items-center gap-2 p-2 text-neutral-500'>
							<div className='animate-spin inline-block size-4 border-2 border-neutral-400 border-t-transparent rounded-full' />
							<Text variant='xsmall'>Loading columns...</Text>
						</div>
					) : headersReady ? (
						<button
							type='button'
							onClick={() => setIsColumnModalOpen(true)}
							className='w-full flex items-center justify-between px-3 py-2.5 bg-white border border-neutral-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left'>
							<div>
								<Text variant='small' className='text-neutral-700 font-medium'>
									{isConfigured ? 'Edit Column Configuration' : 'Configure Columns'}
								</Text>
								{isConfigured ? (
									<Text variant='xsmall' className='text-neutral-400 mt-0.5'>
										Match: {localConfig.matchColumn} &middot; Showing {localConfig.columnsToDisplay.length} columns
									</Text>
								) : (
									<Text variant='xsmall' className='text-neutral-400 mt-0.5'>
										Select match column and columns to display
									</Text>
								)}
							</div>
							<svg
								className='w-4 h-4 text-neutral-400'
								fill='none'
								viewBox='0 0 24 24'
								stroke='currentColor'
								strokeWidth={2}
								aria-hidden='true'>
								<path strokeLinecap='round' strokeLinejoin='round' d='M8.25 4.5l7.5 7.5-7.5 7.5' />
							</svg>
						</button>
					) : null}

					{/* Summary chips */}
					{isConfigured && (
						<div className='flex flex-wrap gap-1'>
							{localConfig.columnsToDisplay.map((col, i) => (
								<span
									key={col}
									className='inline-flex items-center gap-1 px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded text-[11px]'>
									<span className='text-neutral-400'>{i + 1}.</span> {col}
								</span>
							))}
						</div>
					)}
				</div>
			)}

			{headersReady && (
				<ColumnConfigModal
					isOpen={isColumnModalOpen}
					onClose={() => setIsColumnModalOpen(false)}
					allColumns={headers}
					initialMatchColumn={localConfig.matchColumn}
					initialColumnsToDisplay={localConfig.columnsToDisplay}
					onSave={handleColumnModalSave}
				/>
			)}
		</div>
	)
}
